import { Component, TemplateRef, computed, inject, input, viewChild } from '@angular/core';
import { ELanguage } from '@app/app.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  EHeaderClasses,
  ICustomColumn,
  IDatatableConfiguration,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { TStatusBadge } from '@shared/components/status-badge/status-badge.interface';
import { NumberSeparatorUtilitiesService } from '@utilities/number-separator-utilities.service';
import { PercentageUtilitiesService } from '@utilities/percentage-utilities.service';
import { ICtaAnalyticsData } from '../../cta-analytics-table.interface';
import { IKpiCard } from '../cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { EKpiGoalsStatusBadge, IKpiGoalsTableRow } from './cta-analytics-details-kpi-goals-table.interface';

@Component({
  imports: [DatatableComponent, StatusBadgeComponent, TranslateModule],
  selector: 'app-cta-analytics-details-kpi-goals-table',
  styleUrl: './cta-analytics-details-kpi-goals-table.component.scss',
  templateUrl: './cta-analytics-details-kpi-goals-table.component.html',
})
export class CtaAnalyticsDetailsKpiGoalsTableComponent {
  public readonly execution = input.required<ICtaAnalyticsData>();
  public readonly kpiGoalsKpiCards = input.required<IKpiCard[]>();

  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly isAnalyticsPlannedMode = input<boolean>(false);

  public readonly kpiGoalsCustomColumns = computed<ICustomColumn[]>(() => [
    { isSortable: true, name: 'status', template: this.kpiGoalsStatusColumnTemplate() },
    { isSortable: true, name: 'kpi', template: this.kpiGoalsKpiColumnTemplate() },
    { isSortable: true, name: 'current', template: this.kpiGoalsCurrentColumnTemplate() },
    { isSortable: true, name: 'expected', template: this.kpiGoalsExpectedColumnTemplate() },
  ]);

  public readonly plannedModeCustomColumns = computed<ICustomColumn[]>(() => [
    { isSortable: true, name: 'kpi', template: this.kpiGoalsKpiColumnTemplate() },
    { isSortable: true, name: 'expected', template: this.kpiGoalsExpectedColumnTemplate() },
  ]);

  public readonly plannedModeTableColumns: TDatatableColumn<IKpiGoalsTableRow, string>[] = [
    {
      dataViewStrategy: (row) => row.kpiSortValue,
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'kpi',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.expectedSortValue,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'expected',
      isSortable: true,
    },
  ];

  public readonly kpiGoalsDatatableConfig = computed<IDatatableConfiguration<IKpiGoalsTableRow>>(() => ({
    defaultSort: { active: '', direction: '' },
    isClientSide: true,
    isLoaderEnabled: false,
    isPaginationEnabled: false,
    isRowClickEnabled: false,
    isSearchEnabled: false,
    showNoDataText: true,
    skipInitialRequest: true,
    tableMargin: '0 0 -12px -24px',
    tablePadding: this.isAnalyticsPlannedMode() ? '0 4px 24px 24px' : '0 16px 24px 48px',
  }));

  public readonly kpiGoalsTableColumns: TDatatableColumn<IKpiGoalsTableRow, string>[] = [
    {
      dataViewStrategy: (row) => row.statusSortValue,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'status',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.kpiSortValue,
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'kpi',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.currentSortValue,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'current',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.expectedSortValue,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'expected',
      isSortable: true,
    },
  ];

  public readonly kpiGoalsTableRows = computed<IKpiGoalsTableRow[]>(() =>
    this.kpiGoalsKpiCards().map((kpiCard) => this.buildRow(kpiCard)),
  );

  private readonly numberSeparatorUtilities = inject(NumberSeparatorUtilitiesService);
  private readonly percentageUtilities = inject(PercentageUtilitiesService);
  private readonly translate = inject(TranslateService);

  private readonly kpiGoalsCurrentColumnTemplate = viewChild.required<TemplateRef<unknown>>('kpiGoalsCurrentColumn');
  private readonly kpiGoalsExpectedColumnTemplate = viewChild.required<TemplateRef<unknown>>('kpiGoalsExpectedColumn');
  private readonly kpiGoalsKpiColumnTemplate = viewChild.required<TemplateRef<unknown>>('kpiGoalsKpiColumn');
  private readonly kpiGoalsStatusColumnTemplate = viewChild.required<TemplateRef<unknown>>('kpiGoalsStatusColumn');

  private buildRow(kpiCard: IKpiCard): IKpiGoalsTableRow {
    return {
      current: this.formatKpiGoalNumericField(kpiCard, 'value'),
      currentSortValue: kpiCard.value ?? Number.NEGATIVE_INFINITY,
      expected: this.formatKpiGoalNumericField(kpiCard, 'targetValue'),
      expectedSortValue: kpiCard.targetValue ?? Number.NEGATIVE_INFINITY,
      id: kpiCard.id,
      kpi: kpiCard.title ?? '-',
      kpiSortValue: (kpiCard.title ?? '').toLowerCase(),
      status: '',
      statusBadge: this.resolveKpiGoalsStatusBadge(kpiCard),
      statusSortValue: this.getKpiGoalsStatusSortValue(kpiCard),
    };
  }

  private formatKpiGoalNumericField(kpiCard: IKpiCard, field: 'targetValue' | 'value'): string {
    const raw = field === 'value' ? kpiCard.value : kpiCard.targetValue;

    if (raw === null || raw === undefined) {
      return '-';
    }

    if (kpiCard.isPercentage) {
      return `${this.percentageUtilities.getPercentage(raw, true, 2)}%`;
    }

    const language = (this.translate.currentLang as ELanguage) ?? ELanguage.en;

    return this.numberSeparatorUtilities.formatNumberToLocaleString(raw, language, 0, 2, true);
  }

  private getKpiGoalsStatusSortValue(kpiCard: IKpiCard): number {
    if (kpiCard.value === null || kpiCard.value === undefined) {
      return 0;
    }

    if (kpiCard.targetValue === null || kpiCard.targetValue === undefined) {
      return 0;
    }

    return kpiCard.value >= kpiCard.targetValue ? 2 : 1;
  }

  private resolveKpiGoalsStatusBadge(kpiCard: IKpiCard): TStatusBadge | null {
    if (kpiCard.value === null || kpiCard.value === undefined) {
      return EKpiGoalsStatusBadge.kpiGoalsUnavailable;
    }

    if (kpiCard.targetValue === null || kpiCard.targetValue === undefined) {
      return null;
    }

    return kpiCard.value >= kpiCard.targetValue
      ? EKpiGoalsStatusBadge.kpiGoalsReached
      : EKpiGoalsStatusBadge.kpiGoalsBelowTarget;
  }
}
